import React, { useEffect, useState, useRef, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  Empty,
  Spin,
  Typography,
  Card,
  Row,
  Col,
  Carousel,
  Divider,
  Tooltip,
  Tag,
  FloatButton,
  Tabs,
} from "antd";
import { LinkWrapper, AutoCompleteFilters } from "Explore/Components";
import ConceptCard from "Explore/Components/ConceptCard/ConceptCard";
import {
  getExampleConceptExplore,
  getSearchConcepts,
} from "Explore/Services/Concept";
import {
  Newspaper,
  ChartColumn,
  TrendingUp,
  TrendingDown,
  Award,
  ArrowRight,
  Search,
  X,
  Menu,
  Star,
  ArrowUpToLine,
} from "lucide-react";
import styles from "./ConceptList.module.scss";
import { motion, AnimatePresence } from "framer-motion";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { format } from "d3-format";
import { getSuggestKeywords } from "Explore/Services/Common";
import useDeviceCheck from "Hooks/useDeviceCheck";
import useFilterAndPagination from "Hooks/useFilterAndPagination";
import { ParamsFilterKey } from "Config/filterSearchParams";

const { Title, Text, Paragraph } = Typography;

const getRandomColor = () => {
  const hue = Math.floor(Math.random() * 360);
  return `hsl(${hue}, 70%, 95%)`;
};

const titleAnimation = {
  hidden: { opacity: 0, y: -20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const ConceptList = () => {
  const { isMobile } = useDeviceCheck();
  const navigate = useNavigate();
  const [concepts, setConcepts] = useState([]);
  const { filter, updateSearchParams } = useFilterAndPagination();
  // const [filters, setFilters] = useState({
  //   query: [],
  // });
  const [pagination, setPagination] = useState({
    page: 1,
    rowsPerPage: isMobile ? 20 : 99,
    total: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  const carouselRef = useRef(null);
  const featuredRef = useRef(null);
  const exploreRef = useRef(null);

  useEffect(() => {
    fetchConcepts();
  }, [filter, pagination.page, pagination.rowsPerPage]);

  const fetchConcepts = () => {
    setIsLoading(true);
    // getSearchConcepts(
    //   {
    //     query: filters.query,
    //     page: pagination.page - 1,
    //     rowsPerPage: pagination.rowsPerPage,
    //   },
    //   handleConceptResponse
    // );
    if (filter[ParamsFilterKey.QUERY]?.length > 0) {
      getSearchConcepts(
        {
          query: filter[ParamsFilterKey.QUERY],
          page: pagination.page - 1,
          rowsPerPage: pagination.rowsPerPage,
        },
        handleConceptResponse
      );
    } else {
      getExampleConceptExplore(
        pagination.page - 1,
        pagination.rowsPerPage,
        handleConceptResponse
      );
    }
  };

  const calculatePublicationTrend = (yearlyStats) => {
    if (yearlyStats && yearlyStats.length >= 3) {
      const lastTwoYears = yearlyStats.slice(-3, -1);
      const lastYear = lastTwoYears[1];
      const previousYear = lastTwoYears[0];

      if (lastYear && previousYear && previousYear.publications !== 0) {
        return (
          ((lastYear.publications - previousYear.publications) /
            previousYear.publications) *
          100
        );
      }
    }
    return null;
  };

  const handleConceptResponse = (res) => {
    const conceptsWithTrend = res.results.map((concept) => ({
      ...concept,
      publicationTrend: calculatePublicationTrend(concept.yearly_stats),
      yearly_stats: concept.yearly_stats.slice(0, -1),
    }));
    setConcepts(conceptsWithTrend);
    setPagination((prev) => ({
      ...prev,
      total: res.total || 0,
    }));
    setIsLoading(false);
  };

  // const handleSearch = (value) => {
  //   setIsLoading(true);
  //   getSearchConceptsNew(
  //     {
  //       query: value,
  //       page: 0,
  //       rowsPerPage: pagination.rowsPerPage,
  //     },
  //     (res) => {
  //       const conceptsWithTrend = res.results.map((concept) => ({
  //         ...concept,
  //         publicationTrend: calculatePublicationTrend(concept.yearly_stats),
  //       }));
  //       setConcepts(conceptsWithTrend);
  //       setPagination((prev) => ({
  //         ...prev,
  //         page: 1,
  //         total: res.total || 0,
  //       }));
  //       setFilters({ ...filters, query: value });
  //       navigate(`/explore/concept${value ? `?query=${value}` : ""}`);
  //       setIsLoading(false);
  //     }
  //   );
  // };

  const getMostCitedConcepts = (concepts, limit = 5) => {
    return [...concepts]
      .sort((a, b) => b.total_citations - a.total_citations)
      .slice(0, limit);
  };

  const getMostActiveConcepts = (concepts, limit = 5) => {
    return [...concepts]
      .sort((a, b) => {
        const aRecentPubs =
          a.yearly_stats?.[a.yearly_stats.length - 2]?.publications || 0;
        const bRecentPubs =
          b.yearly_stats?.[b.yearly_stats.length - 2]?.publications || 0;
        return bRecentPubs - aRecentPubs;
      })
      .slice(0, limit);
  };

  const renderConceptCard = (concept, isActive) => {
    if (!concept) return null;

    const publicationTrend = concept.publicationTrend;

    const formatNumber = (num) => {
      return num.toLocaleString("en-US");
    };

    const statContent = isActive
      ? [
        {
          title: "Recent Publications",
          value: formatNumber(
            concept.yearly_stats
              ? concept.yearly_stats
                .slice(-5)
                .reduce((sum, year) => sum + (year.publications || 0), 0)
              : 0
          ),
          icon: <Newspaper size={16} />,
        },
        {
          title: "Publication Trend",
          value: `${Math.abs(publicationTrend).toFixed(1)}%`,
          icon:
            publicationTrend >= 0 ? (
              <TrendingUp size={16} color="#4CAF50" />
            ) : (
              <TrendingDown size={16} color="#cf1322" />
            ),
          color: publicationTrend >= 0 ? "#4CAF50" : "#cf1322",
        },
      ]
      : [
        {
          title: "Total Publications",
          value: formatNumber(concept.total_publications || 0),
          icon: <Newspaper size={16} />,
        },
        {
          title: "Total Citations",
          value: formatNumber(concept.total_citations || 0),
          icon: <ChartColumn size={16} />,
        },
      ];

    const chartData = concept.yearly_stats
      ? concept.yearly_stats.slice(-5).map((stat) => ({
        year: stat.year,
        publications: stat.publications,
        citations: stat.citations,
      }))
      : [];

    const formatYAxis = (value) => {
      const formatter = format(".2~s");
      return formatter(value).replace("G", "B");
    };

    const customTooltip = ({ active, payload, label }) => {
      if (active && payload && payload.length) {
        return (
          <div className={styles.customTooltip}>
            <p className={styles.label}>{`Year: ${label}`}</p>
            <p
              className={styles.data}
            >{`Publications: ${payload[0].value.toLocaleString()}`}</p>
            <p
              className={styles.data}
            >{`Citations: ${payload[1].value.toLocaleString()}`}</p>
          </div>
        );
      }
      return null;
    };

    return (
      <motion.div
        whileHover={{
          scale: 1.02,
          transition: { duration: 0.3 },
        }}
      >
        <LinkWrapper
          link={`/explore/concept/${concept.id}`}
          component={
            <Card className={styles.carouselItem}>
              <div className={styles.cardContent}>
                <div className={styles.header}>
                  <Title level={4} className={styles.title}>
                    <span className={styles.titleGradient}>
                      {concept.value.toUpperCase()}
                    </span>
                  </Title>
                </div>
                <Paragraph
                  className={styles.conceptDefinition}
                  ellipsis={{
                    rows: 2,
                    expandable: false,
                    symbol: "...",
                  }}
                >
                  {concept.definition || "No definition available"}
                </Paragraph>
                <Row gutter={[8, 8]} className={styles.conceptStats}>
                  {statContent.map((stat, index) => (
                    <Col span={12} key={index}>
                      <div className={styles.statWrapper}>
                        <div className={styles.statContent}>
                          {stat.icon}
                          <span
                            className={styles.statValue}
                            style={stat.color ? { color: stat.color } : {}}
                          >
                            {stat.value}
                          </span>
                        </div>
                        <span className={styles.statTitle}>{stat.title}</span>
                      </div>
                    </Col>
                  ))}
                </Row>
                <div className={styles.chartWrapper}>
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                    minHeight={150}
                  >
                    {chartData.length > 0 && (
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="year"
                          tick={{ fontSize: 10 }}
                          tickLine={{ stroke: "#ccc" }}
                          axisLine={{ stroke: "#ccc" }}
                        />
                        <YAxis
                          yAxisId="left"
                          tick={{ fontSize: 10 }}
                          tickLine={{ stroke: "#ccc" }}
                          axisLine={{ stroke: "#ccc" }}
                          tickFormatter={formatYAxis}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          tick={{ fontSize: 10 }}
                          tickLine={{ stroke: "#ccc" }}
                          axisLine={{ stroke: "#ccc" }}
                          tickFormatter={formatYAxis}
                        />
                        <RechartsTooltip content={customTooltip} />
                        <Legend
                          verticalAlign="top"
                          height={36}
                          iconSize={10}
                          wrapperStyle={{ fontSize: "12px" }}
                        />
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="citations"
                          stroke="#FF6B6B"
                          dot={false}
                          name="Citations"
                        />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="publications"
                          stroke="#4ECDC4"
                          dot={false}
                          name="Publications"
                        />
                      </LineChart>
                    )}
                  </ResponsiveContainer>
                </div>
                <div className={styles.cardFooter}>
                  <span className={styles.viewMoreButton}>
                    Learn More <ArrowRight size={16} />
                  </span>
                </div>
              </div>
            </Card>
          }
        />
      </motion.div>
    );
  };

  const renderConceptGrid = () => (
    <Row gutter={[24, 48]}>
      {concepts.map((item) => (
        <Col xs={24} sm={12} md={8} key={item.id}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            style={{ height: "100%" }}
          >
            <ConceptCard
              data={item}
              LinkWrapper={LinkWrapper}
              backgroundColor={getRandomColor()}
            />
          </motion.div>
        </Col>
      ))}
    </Row>
  );

  const [activeTab, setActiveTab] = useState("1");

  const handleTabChange = useCallback((key) => {
    setActiveTab(key);
  }, []);

  const tabItems = [
    {
      key: "1",
      label: (
        <span>
          <Award size={18} />
          Most Cited Concepts
        </span>
      ),
    },
    {
      key: "2",
      label: (
        <span>
          <TrendingUp size={18} />
          Most Active Concepts
        </span>
      ),
    },
  ];

  const renderActiveCarousel = useCallback(() => {
    const carouselConcepts =
      activeTab === "1"
        ? getMostCitedConcepts(concepts)
        : getMostActiveConcepts(concepts);
    const isActive = activeTab === "2";

    const settings = {
      dots: true,
      infinite: true,
      speed: 500,
      slidesToShow: 3,
      slidesToScroll: 1,
      arrows: true,
      centerMode: true,
      centerPadding: "0",
      className: `${styles.carousel}`,
      responsive: [
        {
          breakpoint: 1200,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 1,
            centerMode: false,
          },
        },
        {
          breakpoint: 768,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            centerMode: false,
          },
        },
      ],
      afterChange: () => { },
    };

    return (
      <div className={styles.carouselWrapper} ref={carouselRef}>
        <Carousel {...settings}>
          {carouselConcepts.map((concept, index) => (
            <div key={index} className={styles.carouselSlide}>
              {renderConceptCard(concept, isActive)}
            </div>
          ))}
        </Carousel>
      </div>
    );
  }, [activeTab, concepts]);

  const handleResetSearch = () => {
    updateSearchParams({ query: null });
    navigate("/explore/concept");
    fetchConcepts();
  };

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  };

  const renderFeaturedConceptsSection = () => (
    <div className={styles.featuredSectionWrapper}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className={styles.featuredSection}
      >
        <motion.h2
          className={styles.sectionTitle}
          initial="hidden"
          animate="visible"
          variants={titleAnimation}
        >
          Featured Concepts
        </motion.h2>
        <Paragraph className={styles.sectionDescription}>
          Discover trending and influential concepts shaping the research
          landscape
        </Paragraph>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          className={styles.conceptTabs}
          items={tabItems}
        />
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            {renderActiveCarousel()}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </div>
  );

  const renderExplorationSection = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.5 }}
      className={styles.explorationSection}
    >
      <div className={styles.explorationContent}>
        <motion.h2
          className={styles.sectionTitle}
          initial="hidden"
          animate="visible"
          variants={titleAnimation}
        >
          Explore All Concepts
        </motion.h2>
        <Paragraph className={styles.sectionDescription}>
          Discover a wide range of research concepts across various disciplines
        </Paragraph>
        <div className={styles.searchSection}>
          <div className={styles.searchWrapper}>
            <AutoCompleteFilters
              style={{ height: "100%" }}
              placeholder='Search for a concept. Tip: You can use natural language queries like "application of AI in healthcare"'
              functionSearch={getSuggestKeywords}
              filters={filter}
              setFilters={updateSearchParams}
              entity={ParamsFilterKey.QUERY}
            // getFirstItemOnEnter
            // hideFilterSelectedTags
            // callback={handleSearch}
            />
          </div>
          {/* <div className={styles.filterTags}>
            {filters.query.length > 0 && (
              <Tag
                className={styles.queryTag}
                closable
                onClose={handleResetSearch}
                closeIcon={<X size={14} />}
              >
                {filters.query}
              </Tag>
            )}
          </div> */}
        </div>

        <Spin spinning={isLoading}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.7 }}
            className={styles.conceptGrid}
          >
            {concepts.length > 0 ? (
              renderConceptGrid()
            ) : (
              <Empty
                description={
                  <Text>
                    No concepts found. Try adjusting your search criteria.
                  </Text>
                }
              />
            )}
          </motion.div>
          {/* {!isMobile && (
            <div className={styles.paginationWrapper}>
              <PaginationList
                pagination={pagination}
                setPagination={setPagination}
                sizeChanger={true}
                pageSizeOptions={[20, 40, 60, 80]}
              />
            </div>
          )} */}
        </Spin>
      </div>
    </motion.div>
  );

  return (
    <div className={styles["concept-list"]}>
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div ref={featuredRef}>{renderFeaturedConceptsSection()}</div>
      <Divider className={styles.sectionDivider} />
      <div ref={exploreRef}>{renderExplorationSection()}</div>
      <FloatButton.Group
        trigger="hover"
        style={{ right: 24 }}
        icon={<Menu size={24} color="rgb(204, 0, 88)" />}
        closeIcon={<X size={14} color="rgb(204, 0, 88)" />}
        className={styles.floatButtonGroup}
      >
        <Tooltip title="Featured Concepts" placement="left">
          <FloatButton
            icon={<Star size={24} color="rgb(204, 0, 88)" />}
            onClick={() => scrollToSection(featuredRef)}
            className={styles.floatButton}
          />
        </Tooltip>
        <Tooltip title="Explore Concepts" placement="left">
          <FloatButton
            icon={<Search size={24} color="rgb(204, 0, 88)" />}
            onClick={() => scrollToSection(exploreRef)}
            className={styles.floatButton}
          />
        </Tooltip>
        <Tooltip title="Back to Top" placement="left">
          <FloatButton.BackTop
            visibilityHeight={0}
            icon={<ArrowUpToLine size={24} color="rgb(204, 0, 88)" />}
          />
        </Tooltip>
      </FloatButton.Group>
    </div>
  );
};

export default ConceptList;
